import type { SalonService } from "../../../../types/salon";
import type { GetTransactionsResponse } from "../../../../types/transactions";
import {
  BookingMode,
  BookingOrigin,
  ServiceRow,
  emptyRow,
  requestedDay,
  staffServiceFields,
  withDay,
} from "./bookingTypes";
import { isStaffQualified } from "./guards";
import { tempEventRowUid } from "./bookingTempEvents";
import { layBackToBack } from "./draftModel";

/**
 * The booking session's draft. Pure data: every transition goes through
 * bookingDraftReducer.
 *
 * The draft OWNS its one booking day (`day`, salon-local "YYYY-MM-DD").
 * Single-day invariant, enforced here: every row's `date` is `day`, and a
 * date change on any row (a rows update) moves the whole draft to that day.
 * CalendarContext.selectedDate is only the VIEWED day; navigating the
 * calendar never changes the draft.
 */
export interface BookingDraftState {
  open: boolean;
  mode: BookingMode;
  origin: BookingOrigin;
  /** The draft's booking day ("" while closed). */
  day: string;
  /** Source appointment for edit (target) / rebook (rebooking_source_uuid). */
  sourceTxn: GetTransactionsResponse | null;
  rows: ServiceRow[];
  clientUuid: string;
  activeUid: number;
  /** serializeDraft() of the draft as opened, captured by the open action. */
  seedSnapshot: string;
  discardPromptOpen: boolean;
}

export type BookingDraftAction =
  | {
      type: "open";
      mode: BookingMode;
      origin: BookingOrigin;
      sourceTxn: GetTransactionsResponse | null;
      rows: ServiceRow[];
      clientUuid: string;
      /** The draft's day (part of the seed snapshot). */
      day: string;
    }
  | {
      /** A date written on any row moves the whole draft to that day. */
      type: "rowsUpdated";
      update: (rows: ServiceRow[]) => ServiceRow[];
    }
  | { type: "dayChanged"; day: string }
  | { type: "clientChanged"; clientUuid: string }
  | { type: "activeChanged"; uid: number }
  | {
      /** Calendar slot click: place the WHOLE appointment from that time. */
      type: "placeAllAtSlot";
      time: string;
      /** The clicked slot's day; omitted = the draft keeps its day. */
      day?: string;
      /** Staff column clicked (day view), if any. */
      staffId?: string;
      services: SalonService[];
    }
  | {
      type: "moveActiveToSlot";
      time: string;
      /** The clicked slot's day; omitted = the draft keeps its day. */
      day?: string;
      /** Staff column clicked (day view), if any. */
      staffId?: string;
      services: SalonService[];
    }
  | {
      type: "tempDrop";
      uid: number;
      time: string;
      /** The drop's day; omitted = the draft keeps its day. */
      day?: string;
      /** Staff column dropped on (day view), if any. */
      staffId?: string;
      services: SalonService[];
    }
  | { type: "promptDiscard" }
  | { type: "dismissDiscard" }
  | { type: "close" };

export const initialBookingDraft: BookingDraftState = {
  open: false,
  mode: "create",
  origin: "calendar",
  day: "",
  sourceTxn: null,
  rows: [emptyRow(1, null)],
  clientUuid: "",
  activeUid: 1,
  seedSnapshot: "",
  discardPromptOpen: false,
};

/**
 * Meaningful shape of a draft for dirty-tracking: the fields the user can
 * edit, plus the day. Row identity and the active-row selection are excluded
 * (clicking between rows is never a change).
 */
export const serializeDraft = (
  rows: ServiceRow[],
  clientUuid: string,
  day: string,
): string =>
  JSON.stringify({
    day,
    clientUuid: clientUuid || "",
    rows: rows.map((r) => ({
      salonServiceId: r.salonServiceId || "",
      employeeId: r.employeeId || "",
      time: r.time || "",
      baseDuration: r.baseDuration || 0,
      processingTime: r.processingTime || 0,
      finishingTime: r.finishingTime || 0,
      bufferTime: r.bufferTime || 0,
      price: r.price || 0,
      adjustmentOptionIds: [...(r.adjustmentOptionIds || [])].sort(),
    })),
  });

/** True once the draft (rows, client or day) differs from how it opened. */
export const isDraftTouched = (state: BookingDraftState): boolean =>
  serializeDraft(state.rows, state.clientUuid, state.day) !==
  state.seedSnapshot;

/**
 * What a click on a calendar event does while a draft is open:
 * - the draft's own temp event → select its row;
 * - any other event during reschedule / rebook → nothing (no popover, no
 *   prompt);
 * - any other event during a new booking → close and open that event's
 *   preview when the draft is untouched, else the discard confirm first.
 */
export type CalendarEventRoute =
  | { kind: "selectRow"; uid: number }
  | { kind: "ignore" }
  | { kind: "closeAndPreview" }
  | { kind: "promptDiscard" };

export const routeCalendarEventClick = (
  state: BookingDraftState,
  event: unknown,
): CalendarEventRoute => {
  if (!state.open) return { kind: "ignore" };
  const uid = tempEventRowUid(event);
  if (uid !== null) return { kind: "selectRow", uid };
  if (state.mode !== "create") return { kind: "ignore" };
  return isDraftTouched(state)
    ? { kind: "promptDiscard" }
    : { kind: "closeAndPreview" };
};

/**
 * Move a row onto a staff column. Same column / no column → the staff is
 * kept. A provider who can't perform the row's service is rejected (the row
 * keeps its staff). A valid new provider adopts their price/durations.
 */
const moveRowToStaff = (
  row: ServiceRow,
  staffId: string | undefined,
  services: SalonService[],
): ServiceRow => {
  if (!staffId || staffId === row.employeeId) return row;
  const svc = row.salonServiceId
    ? services.find((s) => s.id === row.salonServiceId)
    : undefined;
  if (!isStaffQualified(svc, staffId)) return row;
  return {
    ...row,
    employeeId: staffId,
    ...(svc ? staffServiceFields(svc, staffId) : {}),
  };
};

const activeUidFor = (rows: ServiceRow[], uid: number): number =>
  rows.some((r) => r.uid === uid) ? uid : (rows[0]?.uid ?? 1);

export function bookingDraftReducer(
  state: BookingDraftState,
  action: BookingDraftAction,
): BookingDraftState {
  switch (action.type) {
    case "open": {
      const rows = withDay(
        action.rows.length ? action.rows : [emptyRow(1, null)],
        action.day,
      );
      return {
        open: true,
        mode: action.mode,
        origin: action.origin,
        day: action.day,
        sourceTxn: action.sourceTxn,
        rows,
        clientUuid: action.clientUuid,
        activeUid: rows[0].uid,
        seedSnapshot: serializeDraft(rows, action.clientUuid, action.day),
        discardPromptOpen: false,
      };
    }
    case "rowsUpdated": {
      if (!state.open) return state;
      const updated = action.update(state.rows);
      if (updated === state.rows || updated.length === 0) return state;
      // Single day: a date written on any row is the draft's new day.
      const day = requestedDay(updated, state.day) ?? state.day;
      const rows = withDay(updated, day);
      return {
        ...state,
        day,
        rows,
        activeUid: activeUidFor(rows, state.activeUid),
      };
    }
    case "dayChanged":
      if (!state.open || !action.day || action.day === state.day) return state;
      return {
        ...state,
        day: action.day,
        rows: withDay(state.rows, action.day),
      };
    case "clientChanged":
      if (!state.open || state.clientUuid === action.clientUuid) return state;
      return { ...state, clientUuid: action.clientUuid };
    case "activeChanged":
      if (!state.open || !state.rows.some((r) => r.uid === action.uid)) {
        return state;
      }
      if (state.activeUid === action.uid) return state;
      return { ...state, activeUid: action.uid };
    case "moveActiveToSlot": {
      // One row moves (time + staff), e.g. the mobile reschedule fields; the
      // other rows keep their times. Calendar slot clicks use placeAllAtSlot.
      if (!state.open) return state;
      const activeUid = activeUidFor(state.rows, state.activeUid);
      const day = action.day || state.day;
      return {
        ...state,
        day,
        activeUid,
        rows: withDay(state.rows, day).map((r) =>
          r.uid === activeUid
            ? {
                ...moveRowToStaff(r, action.staffId, action.services),
                time: action.time,
              }
            : r,
        ),
      };
    }
    case "placeAllAtSlot": {
      // Every row moves: the first to the clicked time, the rest back to back
      // in row order. A clicked staff column (day view) becomes the provider
      // of every service they can perform (their price / durations); the
      // others keep theirs. Rows stay independent after (drag / inline edit).
      if (!state.open) return state;
      const day = action.day || state.day;
      const rows = withDay(state.rows, day).map((r) =>
        moveRowToStaff(r, action.staffId, action.services),
      );
      return {
        ...state,
        day,
        activeUid: activeUidFor(state.rows, state.activeUid),
        rows: layBackToBack(rows, action.time),
      };
    }
    case "tempDrop": {
      if (!state.open || !state.rows.some((r) => r.uid === action.uid)) {
        return state;
      }
      const day = action.day || state.day;
      return {
        ...state,
        day,
        activeUid: action.uid,
        rows: withDay(state.rows, day).map((r) =>
          r.uid === action.uid
            ? {
                ...moveRowToStaff(r, action.staffId, action.services),
                time: action.time,
              }
            : r,
        ),
      };
    }
    case "promptDiscard":
      if (!state.open || state.discardPromptOpen) return state;
      return { ...state, discardPromptOpen: true };
    case "dismissDiscard":
      if (!state.discardPromptOpen) return state;
      return { ...state, discardPromptOpen: false };
    case "close":
      return initialBookingDraft;
    default:
      return state;
  }
}
